/**
 * Converts a date string from dd/mm/yyyy format to yyyy-mm-dd (ISO) format
 * for .NET DateOnly serialization. If already in ISO format or null, returns as-is.
 */
export function toIsoDate(value: string | null | undefined): string | null {
    if (!value) return null;
    // Already ISO format (yyyy-mm-dd)
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    // dd/mm/yyyy → yyyy-mm-dd
    const match = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (match) {
        const [, day, month, year] = match;
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return value; // fallback: return as-is
}

/**
 * Converts a date string from yyyy-mm-dd (ISO) format to dd/mm/yyyy for display
 * in masked text inputs. If already in dd/mm/yyyy or null, returns as-is.
 */
export function toDisplayDate(value: string | null | undefined): string | null {
    if (!value) return null;
    // Already dd/mm/yyyy
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(value)) return value;
    // yyyy-mm-dd → dd/mm/yyyy
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) {
        const [, year, month, day] = match;
        return `${day}/${month}/${year}`;
    }
    return value;
}

/**
 * Parses a date string in dd/mm/yyyy or ISO (yyyy-mm-dd, optionally with a time part) format
 * into a local-time Date. ISO dates are read as local calendar dates, not UTC midnight, so the
 * day doesn't shift in negative-offset time zones. Returns null when the value can't be parsed.
 */
export function parseDateString(value: string | null | undefined): Date | null {
    if (!value) return null;
    const dmy = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (dmy) {
        const [, day, month, year] = dmy;
        const d = new Date(+year, +month - 1, +day);
        return Number.isNaN(d.getTime()) ? null : d;
    }
    const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) {
        const [, year, month, day] = iso;
        const d = new Date(+year, +month - 1, +day);
        return Number.isNaN(d.getTime()) ? null : d;
    }
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Age in whole years for a birth date in dd/mm/yyyy or ISO format.
 * Returns null when the date is missing, invalid, or in the future.
 */
export function calcAgeYears(birthDate: string | null | undefined, today: Date = new Date()): number | null {
    const bd = parseDateString(birthDate);
    if (!bd || bd > today) return null;
    let age = today.getFullYear() - bd.getFullYear();
    const m = today.getMonth() - bd.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < bd.getDate())) age--;
    return age;
}
